import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  initialVehicleModels,
  initialEmailTemplates,
  initialAgreementTemplates,
  initialCompanySettings,
} from '../data';
import {
  authService,
  vehicleService,
  customerService,
  rentalService,
  maintService,
  invoiceService,
  damageService,
  refundService,
  dateChangeService,
  notifService,
  roleService,
  userService,
  areaService,
  locationService,
  groupService,
  vehicleTypeService,
  rateRuleService,
  promoCodeService,
  extraService,
  insuranceService,
  poiService,
  agreementService,
  settingsService,
} from '../api/services';
import { userFromApi } from '../api/mappers';

const AppContext = createContext(null);

// localStorage helpers (used only for token and non-API state)
function lsGet(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}
function lsSet(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

// ─── Calendar booking derived from rental ────────────────────────────────────
function calBookingFromRental(r) {
  const start = r.start ? new Date(r.start) : null;
  const end   = r.end   ? new Date(r.end)   : null;
  const days  = start && end ? Math.max(1, Math.ceil((end - start) / 86400000)) : 1;
  const nameParts = (r.customer || '').split(' ');
  const label = nameParts[0] + (nameParts[1] ? ' ' + nameParts[1][0] + '.' : '');
  return {
    id:       'b-' + r.id,
    vid:      r.vid,
    label,
    type:     r.type === 'lease' ? 'lease' : (r.status === 'booked' ? 'booking' : 'rental'),
    start:    start ? start.getDate() : 1,
    span:     days,
    cls:      r.status === 'booked' ? 'cb-b' : 'cb-r',
    rentalId: r.id,
  };
}

// ─────────────────────────────────────────────────────────────────────────────

// ── Token bootstrap ───────────────────────────────────────────────────────────
// The login sub-site places the token in sessionStorage under SESSION_TOKEN_KEY.
// On mount we move it to localStorage (persistent) and clear sessionStorage
// so the next page load won't re-read a stale value.
const SESSION_TOKEN_KEY = process.env.REACT_APP_SESSION_TOKEN_KEY || 'auth_token';
const LOCAL_TOKEN_KEY   = 'auth_token';

function bootstrapToken() {
  try {
    const session = sessionStorage.getItem(SESSION_TOKEN_KEY);
    if (session) {
      localStorage.setItem(LOCAL_TOKEN_KEY, session);
      sessionStorage.removeItem(SESSION_TOKEN_KEY);
      return session;
    }
  } catch {}
  try { return localStorage.getItem(LOCAL_TOKEN_KEY); } catch {}
  return null;
}

// Resolved once when the module loads — moves token from sessionStorage → localStorage if present.
const _initialToken = bootstrapToken();

export function AppProvider({ children }) {
  // ── Auth state ─────────────────────────────────────────────────────────────
  const [authToken, setAuthToken] = useState(_initialToken);
  const [authUser,  setAuthUser]  = useState(null);
  const [loading,   setLoading]   = useState(!!_initialToken);

  // ── Core data (empty until loaded from API) ────────────────────────────────
  const [vehicles,        setVehicles]        = useState([]);
  const [rentals,         setRentals]         = useState([]);
  const [rentalHistory,   setRentalHistory]   = useState([]);
  const [maintHist,       setMaintHist]       = useState([]);
  const [invoices,        setInvoices]        = useState([]);
  const [customers,       setCustomers]       = useState([]);
  const [roles,           setRoles]           = useState([]);
  const [users,           setUsers]           = useState([]);
  const [notifications,   setNotifications]   = useState([]);
  const [locations,       setLocations]       = useState([]);
  const [extras,          setExtras]          = useState([]);
  const [insurance,       setInsurance]       = useState([]);
  const [companySettings, setCompanySettings] = useState(initialCompanySettings);
  const [pois,            setPois]            = useState([]);
  const [refunds,         setRefunds]         = useState([]);
  const [dateChangeRequests, setDateChangeRequests] = useState([]);
  const [groups,          setGroups]          = useState([]);
  const [rateRules,       setRateRules]       = useState([]);
  const [promoCodes,      setPromoCodes]      = useState([]);
  const [vehicleTypes,    setVehicleTypes]    = useState([]);
  const [damages,         setDamages]         = useState([]);
  const [areas,           setAreas]           = useState([]);
  const [agreementTemplates, setAgreementTemplates] = useState(initialAgreementTemplates);
  const [locationLog,     setLocationLog]     = useState([]);

  // Non-API state — persisted to localStorage
  const [vehicleModels,  setVehicleModels]  = useState(() => lsGet('vehicleModels_v1', initialVehicleModels));
  const [emailTemplates, setEmailTemplates] = useState(() => {
    const stored = lsGet('emailTemplates_v1', null);
    if (!stored) return initialEmailTemplates;
    const byTrigger = Object.fromEntries(stored.map(t => [t.trigger, t]));
    return initialEmailTemplates.map(def => byTrigger[def.trigger] || def);
  });
  // Maintain items for legacy maintData (vehicle health cards) — derived from maintHist on demand

  // Persist non-API state
  useEffect(() => { lsSet('vehicleModels_v1', vehicleModels); }, [vehicleModels]);
  useEffect(() => { lsSet('emailTemplates_v1', emailTemplates); }, [emailTemplates]);

  // ── bookings — derived from rentals (calendar events) ─────────────────────
  // Kept as state for compat; updated whenever rentals change
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    setBookings(rentals.map(calBookingFromRental));
  }, [rentals]);

  // maintData — vehicle-level maintenance summary derived from vehicles + maintHist
  const maintData = React.useMemo(() => {
    const byVehicle = {};
    vehicles.forEach(v => {
      byVehicle[v.id] = {
        id: v.id, icon: v.icon || '🔧', name: v.name,
        km: v.odometer || 0, mot: v.motExpiry || '',
        lastDate: '', items: [],
      };
    });
    maintHist.forEach(m => {
      if (byVehicle[m.vid] && m.date > (byVehicle[m.vid].lastDate || '')) {
        byVehicle[m.vid].lastDate = m.date;
      }
    });
    return Object.values(byVehicle);
  }, [vehicles, maintHist]);

  // ── Fetch all data from API ────────────────────────────────────────────────
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        vehiclesData, customersData, rentalsData, historyData,
        maintData, invoicesData, damagesData, notifsData,
        rolesData, usersData, areasData, locationsData,
        groupsData, typeData, ruleData, promoData,
        extData, insData, poisData, agreementsData,
        refundsData, dateChangesData, settingsData,
      ] = await Promise.all([
        vehicleService.list().catch(() => []),
        customerService.list().catch(() => []),
        rentalService.list().catch(() => []),
        rentalService.history().catch(() => []),
        maintService.list().catch(() => []),
        invoiceService.list().catch(() => []),
        damageService.list().catch(() => []),
        notifService.list().catch(() => []),
        roleService.list().catch(() => []),
        userService.list().catch(() => []),
        areaService.list().catch(() => []),
        locationService.list().catch(() => []),
        groupService.list().catch(() => []),
        vehicleTypeService.list().catch(() => []),
        rateRuleService.list().catch(() => []),
        promoCodeService.list().catch(() => []),
        extraService.list().catch(() => []),
        insuranceService.list().catch(() => []),
        poiService.list().catch(() => []),
        agreementService.list().catch(() => []),
        refundService.list().catch(() => []),
        dateChangeService.list().catch(() => []),
        settingsService.get().catch(() => null),
      ]);

      setVehicles(vehiclesData);
      setCustomers(customersData);
      setRentals(rentalsData);
      setRentalHistory(historyData);
      setMaintHist(maintData);
      setInvoices(invoicesData);
      setDamages(damagesData);
      setNotifications(notifsData);
      setRoles(rolesData);
      setUsers(usersData);
      setAreas(areasData);
      setLocations(locationsData);
      setGroups(groupsData);
      setVehicleTypes(typeData);
      setRateRules(ruleData);
      setPromoCodes(promoData);
      setExtras(extData);
      setInsurance(insData);
      setPois(poisData);
      if (agreementsData.length > 0) setAgreementTemplates(agreementsData);
      setRefunds(refundsData);
      setDateChangeRequests(dateChangesData);
      if (settingsData) setCompanySettings(prev => ({ ...prev, ...settingsData }));
    } catch (err) {
      console.error('[FleetRent] Failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Auth events ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      setAuthToken(null);
      setAuthUser(null);
      setVehicles([]); setRentals([]); setCustomers([]);
    };
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  // ── Initial data load ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!authToken) { setLoading(false); return; }
    // Verify token and load user info + all data in parallel
    Promise.all([
      authService.me().catch(() => null),
      fetchAllData(),
    ]).then(([me]) => {
      if (me) setAuthUser(userFromApi(me));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Logout ────────────────────────────────────────────────────────────────
  // Login is handled by the external auth sub-site which stores the token in
  // sessionStorage; this app picks it up via bootstrapToken() on mount.
  const logout = useCallback(async () => {
    try { await authService.logout(); } catch {}
    localStorage.removeItem(LOCAL_TOKEN_KEY);
    setAuthToken(null);
    setAuthUser(null);
    setVehicles([]); setRentals([]); setRentalHistory([]);
    setCustomers([]); setInvoices([]); setDamages([]);
    setNotifications([]); setMaintHist([]);
  }, []);

  // ── UI state (not persisted) ───────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState('rentals');
  const [currentUserId, setCurrentUserId] = useState(() => null);
  const [detailPanel, setDetailPanel] = useState({ open: false, vid: null });
  const [calDetail,   setCalDetail]   = useState({ open: false, bid: null });
  const [toast, setToast] = useState({ show: false, msg: '' });
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => { setIsMobile(e.matches); if (e.matches) setSidebarOpen(false); };
    mq.addEventListener('change', handler);
    if (mq.matches) setSidebarOpen(false);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Set currentUserId from authUser when loaded
  useEffect(() => {
    if (authUser?.id && !currentUserId) setCurrentUserId(authUser.id);
  }, [authUser, currentUserId]);

  // ── Modal state ────────────────────────────────────────────────────────────
  const [modals, setModals] = useState({});

  // ── Toast ──────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg, type) => {
    let t = type;
    if (!t) {
      if (/^(✅|✓|☑)/.test(msg))            t = 'success';
      else if (/^(❌|Error|Failed)/.test(msg)) t = 'error';
      else if (/^(⚠|Warning)/.test(msg))     t = 'warning';
      else if (/^(ℹ|Info)/.test(msg))        t = 'info';
      else                                    t = 'default';
    }
    setToast({ show: true, msg, type: t });
    setTimeout(() => setToast({ show: false, msg: '', type: 'default' }), 5000);
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const openModal  = useCallback((id, payload) => setModals(m => ({
    ...m, [id]: true,
    ...(payload !== undefined ? { [id + '-vid']: payload, [id + '-id']: payload } : {}),
  })), []);
  const closeModal = useCallback((id) => setModals(m => ({ ...m, [id]: false })), []);

  const goto = useCallback((page) => {
    const user = users.find(u => u.id === currentUserId) || users[0];
    const role = roles.find(r => r.id === user?.roleId) || roles[0];
    if (role?.perms?.[page] && role.perms[page].view === false) return;
    setCurrentPage(page);
    setDetailPanel({ open: false, vid: null });
    if (window.innerWidth <= 768) setSidebarOpen(false);
  }, [users, currentUserId, roles]);

  const syncVehicleStatuses = useCallback(() => {
    setVehicles(prev => prev.map(v => {
      const inMaint = maintHist.some(m => m.vid === v.id && m.status === 'in_progress');
      if (inMaint) return { ...v, status: 'maintenance' };
      const active = rentals.find(r => r.vid === v.id && ['active', 'overdue', 'booked'].includes(r.status));
      return active ? { ...v, status: active.status } : { ...v, status: 'available' };
    }));
  }, [rentals, maintHist]);

  const isInMaintenance = useCallback((vid) =>
    maintHist.some(m => m.vid === vid && m.status === 'in_progress')
  , [maintHist]);

  useEffect(() => { syncVehicleStatuses(); }, [maintHist, rentals, syncVehicleStatuses]);

  const getVehicle = useCallback((vid) => vehicles.find(v => v.id === vid) || null, [vehicles]);
  const getRentalById = useCallback((id) => rentals.find(r => r.id === id) || null, [rentals]);
  const getCurrentUser = useCallback(() => users.find(u => u.id === currentUserId) || users[0], [users, currentUserId]);
  const getRole = useCallback((id) => roles.find(r => r.id === id) || roles[0], [roles]);

  const canView = useCallback((section) => {
    const user = users.find(u => u.id === currentUserId) || users[0];
    const role = roles.find(r => r.id === user?.roleId) || roles[0];
    if (role?.locked) return true;
    if (!role?.perms?.[section]) return true;
    return role.perms[section].view !== false;
  }, [users, currentUserId, roles]);

  const canEdit = useCallback((section) => {
    const user = users.find(u => u.id === currentUserId) || users[0];
    const role = roles.find(r => r.id === user?.roleId) || roles[0];
    if (role?.locked) return true;
    if (!role?.perms?.[section]) return true;
    return role.perms[section].edit !== false;
  }, [users, currentUserId, roles]);

  const getCustomerByName = useCallback((name) =>
    customers.find(c => (c.fname + ' ' + c.lname).trim() === name?.trim()) || null
  , [customers]);

  const getAreaForLocation = useCallback((locId) => {
    const loc = locations.find(l => l.id === locId);
    if (!loc?.areaId) return null;
    return areas.find(a => a.id === loc.areaId) || null;
  }, [locations, areas]);

  const transferVehicle = useCallback((vid, toAreaId, reason, rentalId = null) => {
    const vehicle = vehicles.find(v => v.id === vid);
    const fromAreaId = vehicle?.currentAreaId || null;
    if (fromAreaId === toAreaId) return;
    setVehicles(prev => prev.map(v => v.id === vid ? { ...v, currentAreaId: toAreaId } : v));
    setLocationLog(prev => [{
      id: 'tl-' + Date.now(),
      vid, fromAreaId, toAreaId, reason, rentalId,
      timestamp: new Date().toISOString(),
    }, ...prev]);
  }, [vehicles]);

  // ── Notifications ──────────────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    try {
      await notifService.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  // ── Rentals CRUD ───────────────────────────────────────────────────────────
  const [rentalPreset, setRentalPreset] = useState(null);
  const [rentalDraft,  setRentalDraft]  = useState(null);

  const addRental = useCallback(async (rental) => {
    try {
      const created = await rentalService.create(rental);
      setRentals(prev => [created, ...prev]);
      return created;
    } catch (err) {
      showToast('❌ Failed to save rental: ' + (err.message || 'Server error'), 'error');
      // Optimistic fallback — add locally so UI stays consistent
      setRentals(prev => [rental, ...prev]);
      return rental;
    }
  }, [showToast]);

  const endRental = useCallback(async (vid, returnData) => {
    const rental = rentals.find(r => r.vid === vid && ['active', 'overdue'].includes(r.status));
    if (!rental) return;
    const backendId = rental._backendId || rental.id;
    const resolvedReturnLocId = returnData.returnLocId || rental.returnLocId || null;

    // Update local state optimistically
    const completed = { ...rental, ...returnData, status: 'complete' };
    setRentalHistory(h => [completed, ...h]);
    setRentals(prev => prev.filter(r => r.id !== rental.id));
    syncVehicleStatuses();

    if (resolvedReturnLocId) {
      const retLoc = locations.find(l => l.id === resolvedReturnLocId);
      if (retLoc?.areaId) transferVehicle(vid, retLoc.areaId, 'rental_return', rental.id);
    }

    // Persist to API
    try {
      await rentalService.checkOut(backendId, {
        status: 'complete',
        condition_return: returnData.condRet,
        condition_return_note: returnData.condRetNote,
        deposit_refund_mode: returnData.depositReturned === 'Full' ? 'full' : 'partial',
        return_location_id: resolvedReturnLocId ? parseInt(resolvedReturnLocId) : null,
        returned_at: new Date().toISOString(),
        checked_out_at: new Date().toISOString(),
        extra_fee: returnData.extraFee || 0,
        extra_fee_note: returnData.extraFeeNote || null,
      });
    } catch (err) {
      showToast('⚠ Rental ended locally — API sync failed: ' + (err.message || ''), 'warning');
    }
  }, [rentals, syncVehicleStatuses, locations, transferVehicle, showToast]);

  const assignRental = useCallback(async (rentalId, userId, phase = 'checkin') => {
    setRentals(prev => prev.map(r =>
      r.id === rentalId ? { ...r, assignedTo: userId, assignPhase: phase } : r
    ));
    const rental = rentals.find(r => r.id === rentalId);
    const backendId = rental?._backendId || rentalId;
    try {
      await rentalService.assign(backendId, { assigned_to_user_id: parseInt(userId), phase });
    } catch (err) {
      showToast('⚠ Assign sync failed: ' + (err.message || ''), 'warning');
    }
  }, [rentals, showToast]);

  const unassignRental = useCallback(async (rentalId) => {
    setRentals(prev => prev.map(r =>
      r.id === rentalId ? { ...r, assignedTo: null, assignPhase: null } : r
    ));
    const rental = rentals.find(r => r.id === rentalId);
    const backendId = rental?._backendId || rentalId;
    try { await rentalService.unassign(backendId); } catch {}
  }, [rentals]);

  // ── Invoices CRUD ──────────────────────────────────────────────────────────
  const updateInvoiceStatus = useCallback(async (id, status) => {
    setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, status } : inv));
    const inv = invoices.find(i => i.id === id);
    const backendId = inv?._backendId || id;
    try {
      await invoiceService.updateStatus(backendId, status);
    } catch (err) {
      showToast('⚠ Invoice status sync failed: ' + (err.message || ''), 'warning');
    }
  }, [invoices, showToast]);

  const updateInvoice = useCallback((id, updates) => {
    setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, ...updates } : inv));
  }, []);

  const issueInvoice = useCallback(async (id) => {
    const prefix = companySettings.invPrefix || 'INV-';
    const nums = invoices.filter(i => i.num && i.num.startsWith(prefix)).map(i => parseInt(i.num.replace(prefix, '')) || 0);
    const next = Math.max(companySettings.invNext || 1, Math.max(0, ...nums) + 1);
    const num = prefix + String(next).padStart(4, '0');
    setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, num, status: 'issued', issuedAt: new Date().toISOString() } : inv));
    const inv = invoices.find(i => i.id === id);
    const backendId = inv?._backendId || id;
    try {
      await invoiceService.updateStatus(backendId, 'issued');
    } catch {}
    return num;
  }, [invoices, companySettings]);

  const addInvoice = useCallback(async (inv) => {
    try {
      const created = await invoiceService.create(inv);
      setInvoices(prev => [created, ...prev]);
      return created;
    } catch (err) {
      showToast('⚠ Invoice save failed: ' + (err.message || ''), 'warning');
      setInvoices(prev => [inv, ...prev]);
      return inv;
    }
  }, [showToast]);

  // ── Customers CRUD ─────────────────────────────────────────────────────────
  const addCustomer = useCallback(async (customer) => {
    const exists = customers.find(c => c.id === customer.id);
    if (exists) {
      setCustomers(prev => prev.map(c => c.id === customer.id ? customer : c));
      const backendId = exists._backendId || customer.id;
      try { await customerService.update(backendId, customer); } catch {}
      return;
    }
    try {
      const created = await customerService.create(customer);
      setCustomers(prev => [...prev, created]);
    } catch (err) {
      showToast('⚠ Customer save failed: ' + (err.message || ''), 'warning');
      setCustomers(prev => [...prev, customer]);
    }
  }, [customers, showToast]);

  // ── Vehicles CRUD ──────────────────────────────────────────────────────────
  const addVehicle = useCallback(async (vehicle) => {
    try {
      const created = await vehicleService.create(vehicle);
      setVehicles(prev => [...prev, created]);
    } catch (err) {
      showToast('⚠ Vehicle save failed: ' + (err.message || ''), 'warning');
      setVehicles(prev => [...prev, vehicle]);
    }
  }, [showToast]);

  const updateVehicle = useCallback(async (id, updates) => {
    setVehicles(prev => prev.map(v => v.id === id ? { ...v, ...updates } : v));
    const vehicle = vehicles.find(v => v.id === id);
    const backendId = vehicle?._backendId || id;
    try {
      await vehicleService.update(backendId, { ...vehicle, ...updates });
    } catch (err) {
      showToast('⚠ Vehicle update sync failed: ' + (err.message || ''), 'warning');
    }
  }, [vehicles, showToast]);

  // ── Maintenance CRUD ───────────────────────────────────────────────────────
  const addMaintRecord = useCallback(async (record) => {
    try {
      const created = await maintService.create(record);
      setMaintHist(prev => [created, ...prev]);
    } catch (err) {
      showToast('⚠ Maintenance save failed: ' + (err.message || ''), 'warning');
      setMaintHist(prev => [record, ...prev]);
    }
  }, [showToast]);

  const updateMaintRecord = useCallback(async (id, updates) => {
    setMaintHist(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
    const record = maintHist.find(m => m.id === id);
    const backendId = record?._backendId || id;
    try {
      await maintService.update(backendId, { ...record, ...updates });
    } catch {}
  }, [maintHist]);

  // ── Damages CRUD ───────────────────────────────────────────────────────────
  const nextDamageId = useCallback(() => {
    const nums = damages.map(d => parseInt((d.id || '').replace('dmg-', '')) || 0);
    return 'dmg-' + String(Math.max(0, ...nums) + 1).padStart(3, '0');
  }, [damages]);

  const addDamage = useCallback(async (record) => {
    const id = record.id || ('dmg-' + Date.now().toString(36));
    const fullRecord = {
      id, reportedDate: new Date().toISOString().slice(0, 10),
      createdAt: new Date().toISOString(), photos: [],
      status: 'reported', paidBy: 'unpaid', ...record,
    };
    try {
      const created = await damageService.create(fullRecord);
      setDamages(prev => [created, ...prev]);
      return created;
    } catch (err) {
      showToast('⚠ Damage save failed: ' + (err.message || ''), 'warning');
      setDamages(prev => [fullRecord, ...prev]);
      return fullRecord;
    }
  }, [showToast]);

  const updateDamage = useCallback(async (id, updates) => {
    setDamages(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
    const dmg = damages.find(d => d.id === id);
    const backendId = dmg?._backendId || id;
    try {
      await damageService.update(backendId, { ...dmg, ...updates });
    } catch {}
  }, [damages]);

  const deleteDamage = useCallback(async (id) => {
    setDamages(prev => prev.filter(d => d.id !== id));
    const dmg = damages.find(d => d.id === id);
    const backendId = dmg?._backendId || id;
    try { await damageService.delete(backendId); } catch {}
  }, [damages]);

  const getDamagesForVehicle  = useCallback((vid) => damages.filter(d => d.vid === vid), [damages]);
  const getDamagesForRental   = useCallback((rid) => damages.filter(d => d.rentalId === rid), [damages]);
  const getDamagesForCustomer = useCallback((cid) => damages.filter(d => d.customerId === cid), [damages]);

  // ── Agreement templates ────────────────────────────────────────────────────
  const resetAgreementTemplates = useCallback(() => {
    setAgreementTemplates(initialAgreementTemplates);
  }, []);

  const resolveAgreementTemplate = useCallback((vehicleOrVid, contractType = 'rental') => {
    const v = typeof vehicleOrVid === 'string'
      ? vehicles.find(x => x.id === vehicleOrVid)
      : vehicleOrVid;
    const matchesType = (t) => !t?.appliesTo || t.appliesTo === 'both' || t.appliesTo === contractType;

    if (v?.agreementId) {
      const t = agreementTemplates.find(x => x.id === v.agreementId);
      if (t && matchesType(t)) return t;
    }
    if (v?.groupId) {
      const grp = groups.find(g => g.id === v.groupId);
      if (grp?.agreementId) {
        const t = agreementTemplates.find(x => x.id === grp.agreementId);
        if (t && matchesType(t)) return t;
      }
    }
    const defaultMatch = agreementTemplates.find(t => t.isDefault && matchesType(t));
    if (defaultMatch) return defaultMatch;
    const anyMatch = agreementTemplates.find(matchesType);
    if (anyMatch) return anyMatch;
    return agreementTemplates.find(t => t.isDefault) || agreementTemplates[0] || null;
  }, [agreementTemplates, vehicles, groups]);

  // ── Vehicle resolve / confirmation helpers ─────────────────────────────────
  const resolveVehicle = useCallback((v) => {
    if (!v || !v.modelId) return v;
    const m = vehicleModels.find(mm => mm.id === v.modelId);
    if (!m) return v;
    return {
      ...v,
      icon:         m.icon         ?? v.icon,
      type:         m.type         ?? v.type,
      photos:       (m.photos && m.photos.length) ? m.photos : v.photos,
      features:     m.features     ?? v.features,
      transmission: m.transmission ?? v.transmission,
      fuelType:     m.fuelType     ?? v.fuelType,
      seats:        m.seats        ?? v.seats,
      mileage:      m.mileage      ?? v.mileage,
      priceDay:     m.priceDay     ?? v.priceDay,
      deposit:      m.deposit      ?? v.deposit,
      confirm:      m.confirm      ?? v.confirm,
      insuranceMode:   (m.insuranceMode === 'custom' && (m.customInsurance || []).length > 0) ? 'custom' : v.insuranceMode,
      customInsurance: (m.insuranceMode === 'custom' && (m.customInsurance || []).length > 0) ? m.customInsurance : v.customInsurance,
      agreementId:  m.contractId   ?? v.agreementId,
      groupId:      m.groupId      ?? v.groupId,
      _modelName:   m.name,
      _modelId:     m.id,
    };
  }, [vehicleModels]);

  const effectiveConfirmation = useCallback((rawVehicle) => {
    const global = companySettings?.bookingConfirmation || 'manual';
    if (global !== 'auto') return 'manual';
    if (!rawVehicle) return 'manual';
    if (rawVehicle.modelId) {
      const m = vehicleModels.find(mm => mm.id === rawVehicle.modelId);
      if (!m) return 'manual';
      return m.confirm === 'auto' ? 'auto' : 'manual';
    }
    return rawVehicle.confirm === 'auto' ? 'auto' : 'manual';
  }, [companySettings, vehicleModels]);

  // ── ID generators ──────────────────────────────────────────────────────────
  const nextContractId = useCallback(() => {
    const tail = (id) => parseInt(String(id || '').replace(/^CNT-/, '')) || 0;
    const max = Math.max(
      0,
      ...rentals.map(r => tail(r.id)),
      ...(rentalHistory || []).map(r => tail(r.id)),
      ...(invoices || []).map(i => tail(i.contract)),
    );
    return 'CNT-' + String(max + 1).padStart(3, '0');
  }, [rentals, rentalHistory, invoices]);

  const nextInvoiceNum = useCallback(() => {
    const prefix = companySettings.invPrefix || 'INV-';
    const nums = invoices.map(i => parseInt((i.num || '').replace(prefix, '')) || 0);
    const next = Math.max(companySettings.invNext || 1, Math.max(0, ...nums) + 1);
    return prefix + String(next).padStart(4, '0');
  }, [invoices, companySettings]);

  // ── Lease — monthly invoice generation ────────────────────────────────────
  const generateLeaseInvoice = useCallback(async (leaseId) => {
    const lease = rentals.find(r => r.id === leaseId && r.type === 'lease');
    if (!lease || lease.status !== 'active') return null;

    const start = new Date(lease.start);
    const billed = lease.invoicedMonths || [];
    const monthsBilled = billed.length;
    if (monthsBilled >= lease.termMonths) { showToast('All months already invoiced'); return null; }

    const billingDate = new Date(start.getFullYear(), start.getMonth() + monthsBilled, start.getDate());
    const billingKey  = billingDate.toISOString().slice(0, 7);
    if (billed.includes(billingKey)) { showToast('That month is already invoiced'); return null; }

    const monthLabel = billingDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const monthStart = new Date(billingDate.getFullYear(), billingDate.getMonth(), 1);
    const monthEnd   = new Date(billingDate.getFullYear(), billingDate.getMonth() + 1, 0);
    const fmtShort   = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const periodStr  = `${fmtShort(monthStart)} – ${fmtShort(monthEnd)}`;
    const vehicleName = lease.vid ? (vehicles.find(v => v.id === lease.vid)?.name || '') : '';
    const vatRate = parseFloat(lease.vatRate) || parseFloat(companySettings?.vatRate || 19);
    const vatMode = lease.vatMode || companySettings?.vatMode || 'inclusive';
    const monthlyRate = parseFloat(lease.monthlyRate) || 0;

    const subtotal = vatMode === 'inclusive' ? monthlyRate / (1 + vatRate / 100) : monthlyRate;
    const vatAmt   = vatMode === 'inclusive' ? monthlyRate - subtotal : monthlyRate * vatRate / 100;
    const total    = vatMode === 'inclusive' ? monthlyRate : monthlyRate + vatAmt;

    const num = nextInvoiceNum();
    const fmtDate = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const issueDate = billingDate.toISOString().slice(0, 10);
    let dueDays = 14;
    if (lease.billingCadence === 'advance')      dueDays = 0;
    if (lease.billingCadence === 'end_of_month') dueDays = 0;
    if (lease.billingCadence === 'net_15')       dueDays = 15;
    if (lease.billingCadence === 'net_30')       dueDays = 30;
    const dueDate = new Date(billingDate.getFullYear(), billingDate.getMonth(), billingDate.getDate() + dueDays).toISOString().slice(0, 10);

    const methodLabelMap = { bank: 'Bank Transfer', sepa: 'SEPA Direct Debit', stripe: 'Card (Stripe)', paymentlink: 'Payment Link', cash: 'Cash' };
    const invoice = {
      id: 'inv-' + Date.now().toString(36),
      num, status: 'pending', issued: fmtDate(billingDate), issuedAt: new Date().toISOString(),
      customer: lease.customer, customerCompany: lease.customerCompany || null,
      customerVatNumber: lease.vatNumber || null, customerId: lease.customerId || null,
      vehicle: vehicleName, vid: lease.vid, contract: lease.id,
      method: lease.payMethod || 'bank', methodLabel: methodLabelMap[lease.payMethod] || 'Bank Transfer',
      issueDate, dueDate, issue: fmtDate(billingDate), due: fmtDate(new Date(dueDate)),
      lines: [{ desc: `${vehicleName || 'Lease'} · ${periodStr}`, qty: 1, unit: subtotal, total: subtotal }],
      subtotal, vatRate, vatAmt, vatMode, discount: 0, discType: 'eur', total,
      notes: `Lease ${lease.id} · Month ${monthsBilled + 1} of ${lease.termMonths} · ${monthLabel}`,
      isLeaseInvoice: true, leaseMonthKey: billingKey, email: lease.email || '',
    };

    const saved = await addInvoice(invoice);
    setRentals(prev => prev.map(r =>
      r.id === leaseId ? { ...r, invoicedMonths: [...billed, billingKey] } : r
    ));
    showToast(`Lease invoice ${num} created for ${monthLabel}`);
    return saved || invoice;
  }, [rentals, vehicles, companySettings, nextInvoiceNum, showToast, addInvoice]);

  // ── User helpers ───────────────────────────────────────────────────────────
  const isOperatorRole = useCallback(() => {
    const user = users.find(u => u.id === currentUserId) || users[0];
    const role = roles.find(r => r.id === user?.roleId) || roles[0];
    return role?.id === 'operator' || role?.operatorMode === true;
  }, [users, currentUserId, roles]);

  const getUserName = useCallback((userId) => {
    const u = users.find(x => x.id === userId);
    return u ? `${u.fname} ${u.lname}` : '';
  }, [users]);

  // ── Demo reset (clears local-only state) ──────────────────────────────────
  const resetDemoData = useCallback(() => {
    localStorage.removeItem('vehicleModels_v1');
    localStorage.removeItem('emailTemplates_v1');
    fetchAllData();
  }, [fetchAllData]);

  // ── Context value ──────────────────────────────────────────────────────────
  const value = {
    // Auth
    authToken, authUser, loading, logout,

    // Data
    vehicles, setVehicles,
    rentals, setRentals,
    bookings, setBookings,
    rentalHistory, setRentalHistory,
    maintData, // derived — read-only
    maintHist, setMaintHist,
    invoices, setInvoices,
    customers, setCustomers,
    roles, setRoles,
    users, setUsers,
    notifications, setNotifications,
    locations, setLocations,
    extras, setExtras,
    insurance, setInsurance,
    companySettings, setCompanySettings,
    pois, setPois,
    groups, setGroups,
    rateRules, setRateRules,
    promoCodes, setPromoCodes,
    vehicleTypes, setVehicleTypes,
    vehicleModels, setVehicleModels,
    resolveVehicle, effectiveConfirmation,
    damages, setDamages,
    addDamage, updateDamage, deleteDamage,
    getDamagesForVehicle, getDamagesForRental, getDamagesForCustomer,
    nextDamageId,
    generateLeaseInvoice,
    emailTemplates, setEmailTemplates,
    areas, setAreas,
    locationLog, setLocationLog,
    agreementTemplates, setAgreementTemplates, resetAgreementTemplates,
    resolveAgreementTemplate,
    resetDemoData, fetchAllData,
    refunds, setRefunds,
    dateChangeRequests, setDateChangeRequests,
    rentalPreset, setRentalPreset,
    rentalDraft, setRentalDraft,

    // UI
    sidebarOpen, setSidebarOpen, isMobile,
    currentPage, goto,
    currentUserId, setCurrentUserId,
    detailPanel, setDetailPanel,
    calDetail, setCalDetail,
    toast,
    notifPanelOpen, setNotifPanelOpen,
    modals, openModal, closeModal,

    // Actions
    showToast,
    syncVehicleStatuses,
    getVehicle,
    getRentalById,
    getCurrentUser,
    getRole,
    getCustomerByName,
    markAllRead,
    unreadCount,
    addRental,
    endRental,
    updateInvoiceStatus,
    updateInvoice,
    issueInvoice,
    addInvoice,
    addCustomer,
    addVehicle,
    updateVehicle,
    addMaintRecord,
    updateMaintRecord,
    isInMaintenance,
    assignRental,
    unassignRental,
    isOperatorRole,
    getUserName,
    nextContractId,
    nextInvoiceNum,
    getAreaForLocation,
    transferVehicle,
    canView,
    canEdit,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}
